import React, { Fragment, useRef } from 'react';
import { Helmet } from 'react-helmet';

import HeaderV2 from '../../components/headerV2/HeaderV2';
import FooterV2 from '../../components/footerV2/FooterV2';
import AboutHero from '../../components/aboutHero/AboutHero';
import useScrollReveal from '../../hooks/useScrollReveal';

import './galleryV2.css';

/* Production chrome for a live gallery page (jjfv2 design system): Helmet (with
   the Anton/Inter fonts the headings need), HeaderV2, hero with breadcrumb,
   centered heading, footer. The heading sits in a .wrap; `children` render
   directly in the section so the gallery can be .wrap-constrained or full-bleed.
   Unlike GalleryDemoLayout, this carries no "Demo" labelling — it is for the
   real, selected gallery pages. */
/* With `introImage` the kicker/heading/lead become a photo-and-copy intro section
   (same layout as the About pages) and the gallery gets its own section below it. */
const GalleryPageLayout = ({
    title, kicker, heading, lead, metaTitle, metaDescription,
    introImage, introImageAlt, introImagePlain,
    galleryKicker = "Photo gallery", galleryHeading = "Moments from the ground",
    children,
}) => {
    const rootRef = useRef(null);
    useScrollReveal(rootRef);

    return (
        <Fragment>
            <Helmet>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
                    rel="stylesheet"
                />
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
            </Helmet>

            <div className="jjfv2 gallery-live" ref={rootRef}>
                <HeaderV2 />

                <main className="page_content">
                    <AboutHero
                        eyebrow="Gallery"
                        title={title}
                        crumbs={[
                            { label: 'Home', to: '/' },
                            { label: 'Gallery' },
                            { label: title },
                        ]}
                    />

                    {introImage ? (
                        <Fragment>
                            <section className="section about" id="gallery-intro" style={{ paddingTop: "36px", paddingBottom: "36px" }}>
                                <div className="wrap split" style={{ position: "relative", zIndex: 1 }}>
                                    <div className="about-media reveal">
                                        {/* introImagePlain: a plain rounded frame at the photo's own 4:3 shape,
                                            for wide photos that the leaf shape would clip. */}
                                        <span
                                            className="leaf-frame"
                                            aria-hidden="true"
                                            style={introImagePlain ? { borderRadius: "calc(var(--r) + 14px)" } : undefined}
                                        ></span>
                                        <div className="ph" style={introImagePlain ? { borderRadius: "var(--r)", aspectRatio: "4 / 3" } : undefined}>
                                            <img src={introImage} alt={introImageAlt || title} loading="lazy" />
                                        </div>
                                    </div>
                                    <div className="about-copy reveal d1">
                                        <span className="eyebrow">{kicker}</span>
                                        <h2>{heading}</h2>
                                        {lead && <p className="lead">{lead}</p>}
                                    </div>
                                </div>
                            </section>

                            <section className="section section--mist" id="gallery-photos" style={{ paddingTop: "clamp(88px, 10vw, 150px)", paddingBottom: "clamp(88px, 10vw, 150px)" }}>
                                <div className="wrap">
                                    <div className="gal-head reveal">
                                        <span className="eyebrow">{galleryKicker}</span>
                                        <h2>{galleryHeading}</h2>
                                    </div>
                                </div>
                                {children}
                            </section>
                        </Fragment>
                    ) : (
                        <section className="section">
                            <div className="wrap">
                                <div className="gal-head">
                                    <span className="eyebrow">{kicker}</span>
                                    <h2>{heading}</h2>
                                    {lead && <p>{lead}</p>}
                                </div>
                            </div>
                            {children}
                        </section>
                    )}
                </main>

                <FooterV2 />
            </div>
        </Fragment>
    );
};

export default GalleryPageLayout;
